import { useCallback, useId, useImperativeHandle, useMemo, useRef, useState, type ForwardedRef } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { usePaAssistMode } from "@/components/activity/personalAssistant/usePaAssistMode";
import {
  canShowPersonalAssistantToggle,
  isPersonalAssistantModeActive,
} from "@/lib/personalAssistant/paModeGate";
import { useActivityDockAssistLevel } from "@/components/activity/useActivityDockAssistLevel";
import {
  buildFireflyTourRequestEventDetail,
  emitFireflyTourRequestEvent,
} from "@/lib/firefly/fireflyTourRequestEvent";
import { FIREFLY_ASK_PILOT_ARTIFACTS } from "@/lib/firefly/pilotAskArtifacts";
import { FIREFLY_TOUR_CATALOG, getFireflyTourById } from "@/lib/firefly/tourCatalog";
import { fireflyTourRequestOutcome } from "@/lib/firefly/resolveFireflyTour";
import { resolveFireflyDockQuery } from "@/lib/firefly/resolveFireflyDockQuery";
import {
  fireflyAskOutcome,
  type FireflyAskResolution,
} from "@/lib/firefly/resolveFireflyAsk";
import type {
  FireflyInputModality,
  FireflyPttTrigger,
  FireflyTourArtifact,
  FireflyTourTrigger,
} from "@/lib/firefly/tourTypes";
import type { FireflyAskHighlightCallout } from "../ActivityDockAskFireflyResult";
import type { AccessibleFireflyTour } from "@/lib/firefly/listAccessibleFireflyTours";
import { useFireflyAskCatalog } from "./useFireflyAskCatalog";

export type FireflyAskSubmitOptions = {
  inputModality?: FireflyInputModality;
  pttTrigger?: FireflyPttTrigger;
};

export type FireflyAskHighlightRequest = {
  targetId: string;
  callout?: FireflyAskHighlightCallout;
};

export type ActivityDockAskFireflyProps = {
  currentMode: PrimaryMode | null;
  scopedOrganizationId?: string | null;
  onNavigateToMode?: (mode: PrimaryMode) => void;
  onCloseDock?: () => void;
  onHighlightTarget?: (request: FireflyAskHighlightRequest) => void;
  onStartTour?: (artifact: FireflyTourArtifact, trigger?: FireflyTourTrigger) => void;
  /** Tab long-press PTT mic active — syncs Ask listening chrome (PTT-4). */
  tabPttListening?: boolean;
  /**
   * `card` — legacy full-width section with Ask/Hide.
   * `pane-ask` / `pane-pa` — tab panes (always expanded, compact chrome).
   */
  presentation?: "card" | "pane-ask" | "pane-pa";
};

export type ActivityDockAskFireflyHandle = {
  expandForPtt: () => void;
  submitQuery: (options?: FireflyAskSubmitOptions) => void;
  appendPttChunk: (chunk: string) => void;
};

type AskFireflyUiResolution =
  | { kind: "idle" }
  | { kind: "catalog"; entries: AccessibleFireflyTour[] }
  | { kind: "tour"; message: string }
  | { kind: "ask"; resolution: FireflyAskResolution };

export function useActivityDockAskFireflyController(
  {
    currentMode,
    scopedOrganizationId = null,
    onNavigateToMode,
    onCloseDock,
    onHighlightTarget,
    onStartTour,
    tabPttListening = false,
  }: ActivityDockAskFireflyProps,
  ref: ForwardedRef<ActivityDockAskFireflyHandle>,
) {
  const auth = useAuth();
  const getToken = useCallback(async (): Promise<string | null> => {
    if (auth.status !== "ready") return null;
    return (auth.session as unknown as { access_token?: string }).access_token ?? null;
  }, [auth]);
  const hasCapability = useUserCapabilities();
  const { assistMode, setAssistMode } = usePaAssistMode();
  const { level: assistLevel } = useActivityDockAssistLevel();
  const inputId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [uiResolution, setUiResolution] = useState<AskFireflyUiResolution>({ kind: "idle" });
  const [busy, setBusy] = useState(false);
  const [pttListening, setPttListening] = useState(false);

  const appendDictation = useCallback((chunk: string) => {
    const trimmed = chunk.trim();
    if (!trimmed) return;
    setQuery((prev) => (prev ? prev.replace(/\s+$/, "") + " " + trimmed : trimmed));
  }, []);

  const disabled = auth.status !== "ready" || currentMode == null;

  const paGateInput = useMemo(
    () => ({
      authReady: auth.status === "ready",
      impersonationActive: auth.impersonation.active,
      personalAssistantEnabled: auth.personalAssistantEnabled,
      hasRole: (...roles: string[]) => auth.hasRole(...roles),
      hasPersonalAssistantCapability: hasCapability("is_personal_assistant"),
      assistMode,
    }),
    [assistMode, auth, hasCapability],
  );

  const canShowPaToggle = canShowPersonalAssistantToggle(paGateInput);
  const isPaModeActive = isPersonalAssistantModeActive(paGateInput);

  const onPaToggleChange = useCallback(
    (enabled: boolean) => {
      setAssistMode(enabled ? "personal_assistant" : "default");
      if (enabled) setExpanded(true);
    },
    [setAssistMode],
  );

  const exitPaMode = useCallback(() => {
    setAssistMode("default");
  }, [setAssistMode]);

  const highlightWithCallout = useCallback(
    (targetId: string, callout: FireflyAskHighlightCallout) => {
      onCloseDock?.();
      onHighlightTarget?.({ targetId, callout });
    },
    [onCloseDock, onHighlightTarget],
  );

  const applyCatalogUi = useCallback((next: AskFireflyUiResolution) => {
    setUiResolution(next);
  }, []);

  const { openCatalog, startTourFromCatalog, closeCatalog, openCatalogFromAskQuery } =
    useFireflyAskCatalog({
      auth,
      currentMode,
      disabled,
      assistLevel,
      hasCapability,
      onCloseDock,
      onStartTour,
      setExpanded,
      applyCatalogUi: (next) => applyCatalogUi(next),
    });

  const submitQuery = useCallback((options?: FireflyAskSubmitOptions) => {
    const q = query.trim();
    if (!q || disabled || currentMode == null) return;
    setBusy(true);
    const inputModality = options?.inputModality ?? "text";

    const dockResult = resolveFireflyDockQuery({
      queryText: q,
      tours: FIREFLY_TOUR_CATALOG,
      asks: FIREFLY_ASK_PILOT_ARTIFACTS,
      auth,
      currentMode,
      canAccessPrimaryMode,
      hasCapability,
    });

    if (dockResult.route === "catalog") {
      openCatalogFromAskQuery();
      setBusy(false);
      return;
    }

    if (dockResult.route === "tour") {
      const tourResult = dockResult.tour;
      emitFireflyTourRequestEvent(
        buildFireflyTourRequestEventDetail({
          tourId: tourResult.artifact.id,
          queryText: q,
          trigger: "ask",
          intent: tourResult.artifact.defaultIntent,
          mode: currentMode,
          targetMode: tourResult.targetMode,
          outcome: fireflyTourRequestOutcome(tourResult),
          assistLevel,
          nowMs: Date.now(),
          inputModality,
          pttTrigger: options?.pttTrigger,
        }),
      );
      onCloseDock?.();
      onStartTour?.(tourResult.artifact);
      setUiResolution({
        kind: "tour",
        message: `Starting ${tourResult.artifact.id.replace(/^tour-/, "").replace(/-/g, " ")} tour — follow the highlights. Dismiss anytime.`,
      });
      setBusy(false);
      return;
    }

    const askResult = dockResult.ask;

    emitFireflyTourRequestEvent(
      buildFireflyTourRequestEventDetail({
        tourId:
          askResult.kind === "answer" || askResult.kind === "denied"
            ? askResult.artifactId
            : undefined,
        queryText: q,
        trigger: "ask",
        intent: "teaching",
        mode: currentMode,
        targetMode:
          askResult.kind === "answer"
            ? askResult.navigateTo?.mode
            : askResult.kind === "denied"
              ? askResult.targetMode
              : undefined,
        outcome: fireflyAskOutcome(askResult),
        assistLevel,
        nowMs: Date.now(),
        inputModality,
        pttTrigger: options?.pttTrigger,
      }),
    );

    setUiResolution({ kind: "ask", resolution: askResult });
    setBusy(false);
  }, [
    assistLevel,
    auth,
    currentMode,
    disabled,
    hasCapability,
    onCloseDock,
    onStartTour,
    openCatalogFromAskQuery,
    query,
  ]);

  const expandForPtt = useCallback(() => {
    setExpanded(true);
    window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      expandForPtt,
      submitQuery,
      appendPttChunk: appendDictation,
    }),
    [appendDictation, expandForPtt, submitQuery],
  );

  const onNavigate = useCallback(() => {
    if (uiResolution.kind !== "ask" || uiResolution.resolution.kind !== "answer") return;
    const { resolution } = uiResolution;
    if (!resolution.navigateTo) return;
    onCloseDock?.();
    onNavigateToMode?.(resolution.navigateTo.mode);
    if (resolution.highlightTarget) {
      window.setTimeout(() => {
        onHighlightTarget?.({
          targetId: resolution.highlightTarget!,
          callout: { title: "Here's how", body: resolution.answer },
        });
      }, 450);
    }
  }, [onCloseDock, onHighlightTarget, onNavigateToMode, uiResolution]);

  const onStartCompanionTour = useCallback(() => {
    if (uiResolution.kind !== "ask" || uiResolution.resolution.kind !== "answer") return;
    const tourId = uiResolution.resolution.companionTourId;
    if (!tourId || currentMode == null) return;
    const artifact = getFireflyTourById(tourId);
    if (!artifact) return;
    onCloseDock?.();
    onStartTour?.(artifact);
    setUiResolution({
      kind: "tour",
      message: `Starting ${artifact.id.replace(/^tour-/, "").replace(/-/g, " ")} tour — follow the highlights. Dismiss anytime.`,
    });
  }, [currentMode, onCloseDock, onStartTour, uiResolution]);

  return {
    assistLevel,
    auth,
    appendDictation,
    busy,
    canShowPaToggle,
    isPaModeActive,
    assistMode,
    onPaToggleChange,
    currentMode,
    disabled,
    expanded,
    getToken,
    inputId,
    pttListening,
    query,
    scopedOrganizationId,
    setExpanded,
    setPttListening,
    setQuery,
    submitQuery,
    openCatalog,
    startTourFromCatalog,
    closeCatalog,
    tabPttListening,
    textareaRef,
    uiResolution,
    onNavigate,
    onStartCompanionTour,
    highlightWithCallout,
    exitPaMode,
    onNavigateToMode,
  };
}
